import axios, { AxiosResponse } from "axios";
import useSWR, { mutate, SWRConfiguration } from "swr";
import { VidletApiError } from "types/Errors";
import getHash from "utils/getHash";

export interface UseApiGetByPostOptions {
  swrOptions?: SWRConfiguration;
}

const DEFAULT_SWR_OPTIONS: SWRConfiguration = {
  dedupingInterval: 1000 * 5, // 5 seconds
  revalidateOnFocus: false,
};

// https://swr.vercel.app/docs/revalidation#disable-automatic-revalidations
export const SWR_OPTION: { [key: string]: SWRConfiguration } = {
  CALL_ONCE: {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  },
};

export function getApiGetByPostKey(url: string, payload?: unknown) {
  return url ? `${url}#${getHash(payload)}` : "";
}

/** Imperative POST-as-GET: always network-fetch and refresh the SWR cache entry. */
export async function apiGetByPost<RESPONSE>(
  url: string,
  payload?: unknown,
): Promise<RESPONSE> {
  if (!url) {
    throw new Error("apiGetByPost requires a url");
  }
  const key = getApiGetByPostKey(url, payload);
  const response = await mutate<AxiosResponse<RESPONSE>>(
    key,
    async () => await axios.post<RESPONSE>(url, payload),
    { revalidate: false },
  );
  if (!response) {
    throw new Error(`apiGetByPost received no data for ${url}`);
  }
  return response.data;
}

export function revalidateApiGetByPostKeys(
  keyMatcher: (key: string) => boolean,
) {
  void mutate((key) => typeof key === "string" && keyMatcher(key), undefined, {
    revalidate: true,
  });
}

export default function useApiGetByPost<RESPONSE>(
  url: string,
  payload?: unknown,
  options?: UseApiGetByPostOptions,
) {
  const { data, ...rest } = useSWR<AxiosResponse<RESPONSE>, VidletApiError>(
    getApiGetByPostKey(url, payload),
    async () => await axios.post<RESPONSE>(url, payload),
    {
      ...DEFAULT_SWR_OPTIONS,
      ...options?.swrOptions,
    },
  );

  return {
    result: data?.data,
    ...rest,
  };
}
